import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from '../utils/toast';
import PasteUploadArea from './PasteUploadArea';

// Simplified MasterDataSelector for CustomPageRenderer
const SimpleMasterDataSelector = ({ value, onChange, masterDataList, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  console.log('SimpleMasterDataSelector props:', { value, masterDataList, placeholder });
  console.log('Master data list length:', masterDataList?.length);
  console.log('Master data list sample:', masterDataList?.slice(0, 2));

  const filteredData = masterDataList.filter(item => 
    item.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.clientAddress?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  console.log('Search term:', searchTerm);
  console.log('Filtered data length:', filteredData.length);
  console.log('Filtered data sample:', filteredData.slice(0, 2));

  const handleSelect = (item) => {
    console.log('Selected item:', item);
    onChange(item);
    setShowDropdown(false);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder || "Search for a client..."}
        value={value ? value.clientName : searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => {
          console.log('Input focused, showing dropdown');
          setShowDropdown(true);
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      
      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredData.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {masterDataList.length === 0 ? 'No master data available' : 'No clients found'}
            </div>
          ) : (
            <div className="py-1">
              {filteredData.map((item) => (
                <button
                  key={item._id}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                >
                  <div className="font-medium text-gray-900">{item.clientName}</div>
                  {item.clientAddress && (
                    <div className="text-sm text-gray-600">{item.clientAddress}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

// Simplified UserSelector for CustomPageRenderer
const SimpleUserSelector = ({ value, onChange, userList, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredUsers = userList.filter(user => 
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (user) => {
    onChange(user);
    setShowDropdown(false);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder || "Search for a user..."}
        value={value ? `${value.firstName} ${value.lastName}` : searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setShowDropdown(true)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      
      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No users found</div>
          ) : (
            <div className="py-1">
              {filteredUsers.map((user) => (
                <button
                  key={user._id}
                  type="button"
                  onClick={() => handleSelect(user)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                >
                  <div className="font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                  <div className="text-sm text-gray-600">{user.email}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

const CustomPageRenderer = () => {
  const { pageId } = useParams();
  const { user, checkPermission } = useAuth();
  const navigate = useNavigate();
  
  const [customPage, setCustomPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [submissionName, setSubmissionName] = useState('');
  const [masterDataList, setMasterDataList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [documentVerifications, setDocumentVerifications] = useState([]);
  const [documentUploads, setDocumentUploads] = useState({});

  const canCreate = checkPermission('Create Custom Page Submission');
  const canUpdate = checkPermission('Update Custom Page Submission');

  // Check if user has access to this page
  const hasPageAccess = () => {
    if (!customPage) return false;
    
    // Check if user is in accessUsers
    if (customPage.accessUsers && customPage.accessUsers.length > 0) {
      const userInAccessList = customPage.accessUsers.some(userId => 
        userId === user.id || userId._id === user.id
      );
      if (userInAccessList) return true;
    }
    
    // Check if user is in accessGroups
    if (customPage.accessGroups && customPage.accessGroups.length > 0) {
      const userInAccessGroup = customPage.accessGroups.some(groupId => 
        user.groups && user.groups.some(userGroup => 
          userGroup === groupId || userGroup._id === groupId
        )
      );
      if (userInAccessGroup) return true;
    }
    
    // If no access control is set, allow access
    if ((!customPage.accessUsers || customPage.accessUsers.length === 0) && 
        (!customPage.accessGroups || customPage.accessGroups.length === 0)) {
      return true;
    }
    
    return false;
  };

  useEffect(() => {
    fetchCustomPage();
    fetchMasterData();
    fetchUsers();
  }, [pageId]);

  const fetchDocumentVerifications = async () => {
    try {
      const response = await api.get('/custom-documents');
      if (response.data.success) {
        const docs = response.data.data.docs || response.data.data || [];
        setDocumentVerifications(docs);
        
        // Get required Checklist for this custom page
        const requiredDocs = getRequiredDocumentVerifications();
        
        // Initialize document uploads with the actual uploaded files from custom documents
        const initialUploads = {};
        for (const doc of requiredDocs) {
          if (doc.file) {
            // Convert the uploaded document to the format expected by single file structure
            initialUploads[doc._id] = {
              fileName: doc.file.name,
              fileUrl: doc.file.url,
              originalName: doc.file.originalName,
              mimeType: doc.file.mimeType,
              size: doc.file.size
            };
          }
        }
        setDocumentUploads(initialUploads);
      }
    } catch (error) {
      console.error('Error fetching Checklist:', error);
    }
  };

  const fetchCustomPage = async () => {
    try {
      const response = await api.get(`/custom-pages/${pageId}`);
      if (response.data.success) {
        setCustomPage(response.data.data);
        // Initialize form data with empty values
        const initialData = {};
        response.data.data.fields.forEach(field => {
          if (field.type === 'dropdown') {
            initialData[field.name] = '';
          } else if (field.type === 'multitext') {
            initialData[field.name] = '';
          } else if (field.type === 'upload') {
            initialData[field.name] = [];
          } else if (field.type === 'masterdata') {
            initialData[field.name] = null;
          } else if (field.type === 'user') {
            initialData[field.name] = null;
          } else {
            initialData[field.name] = '';
          }
        });
        setFormData(initialData);
        
        // Fetch Checklist after custom page is loaded
        await fetchDocumentVerifications();
      }
    } catch (error) {
      console.error('Error fetching custom page:', error);
      toast.error('Failed to fetch page configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      const response = await api.get('/master-data');
      console.log('Master data response:', response.data);
      // The master data API returns data directly, not wrapped in success property
      setMasterDataList(response.data);
      console.log('Master data list set:', response.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      if (response.data.success) {
        setUserList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }
  };

  const handleDocumentUpload = (documentId, files) => {
    setDocumentUploads(prev => ({
      ...prev,
      [documentId]: files
    }));
  };

  const uploadFileToServer = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return {
          fileName: file.name,
          fileUrl: response.data.url,
          originalName: file.name,
          mimeType: file.type,
          size: file.size
        };
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      return null;
    }
  };

  const handleFileUpload = async (documentId, files) => {
    try {
      console.log('handleFileUpload called with:', { documentId, files });
      
      // If no files provided, clear the upload
      if (!files || files.length === 0) {
        console.log('No files provided, clearing document uploads for', documentId);
        handleDocumentUpload(documentId, null);
        return;
      }
      
      // Take the first file only (since we're using single file structure)
      const file = files[0];
      console.log('Processing file:', file);
      
      // If the file is already a File object (new upload), upload it
      if (file instanceof File) {
        console.log('Uploading new file to server:', file.name);
        const uploadedFile = await uploadFileToServer(file);
        if (uploadedFile) {
          console.log('File uploaded successfully:', uploadedFile);
          handleDocumentUpload(documentId, uploadedFile);
        }
      } else {
        // If it's already an uploaded file object, keep it as is
        console.log('File already uploaded, keeping as is:', file);
        handleDocumentUpload(documentId, file);
      }
    } catch (error) {
      console.error('Error handling file upload:', error);
      toast.error('Failed to upload files');
    }
  };

  const getRequiredDocumentVerifications = () => {
    if (!customPage || !customPage.documentVerifications || !documentVerifications.length) {
      return [];
    }
    
    return documentVerifications.filter(doc => 
      customPage.documentVerifications.some(requiredDoc => 
        requiredDoc === doc._id || requiredDoc._id === doc._id
      )
    );
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate submission name
    if (!submissionName || submissionName.trim() === '') {
      newErrors.name = 'Submission name is required';
    }
    
    customPage.fields.forEach(field => {
      if (field.required) {
        const value = formData[field.name];
        if (!value || (Array.isArray(value) && value.length === 0) || 
            (typeof value === 'string' && value.trim() === '')) {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const submissionData = {
        name: submissionName.trim(),
        data: formData,
        status: 'submitted'
      };

      // Add Checklist if any are required
      const requiredDocs = getRequiredDocumentVerifications();
      if (requiredDocs.length > 0) {
        submissionData.documentVerifications = requiredDocs.map(doc => {
          const uploadedFile = documentUploads[doc._id];
          
          // If file was uploaded, use the uploaded file URL
          // Otherwise, use the custom document's file URL as default
          const fileUrl = uploadedFile 
            ? uploadedFile.fileUrl 
            : (doc.file ? doc.file.url : null);
          
          const fileName = uploadedFile 
            ? uploadedFile.fileName 
            : (doc.file ? doc.file.name : doc.name);
          
          return {
            documentId: doc._id,
            documentName: doc.name,
            fileUrl: fileUrl,
            fileName: fileName,
            files: uploadedFile ? [uploadedFile] : []
          };
        });
      }

      const response = await api.post(`/custom-pages/${pageId}/submissions`, submissionData);
      
      if (response.data.success) {
        toast.success('Form submitted successfully');
        // Redirect to submissions list
        navigate(`/admin/settings/custom-page-submissions/${pageId}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.name];
    const error = errors[field.name];

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.name, parseFloat(e.target.value) || '')}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select an option</option>
            {(field.options || []).map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multitext':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'upload':
        return (
          <PasteUploadArea
            files={value}
            onFilesChange={(files) => handleInputChange(field.name, files)}
            multiple={true}
            accept="*/*"
          />
        );

      case 'masterdata':
        return (
          <SimpleMasterDataSelector
            value={value}
            onChange={(masterData) => handleInputChange(field.name, masterData)}
            masterDataList={masterDataList}
            placeholder={field.placeholder}
          />
        );

      case 'user':
        return (
          <SimpleUserSelector
            value={value}
            onChange={(user) => handleInputChange(field.name, user)}
            userList={userList}
            placeholder={field.placeholder}
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customPage) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
        <p className="text-gray-600">The requested page could not be found.</p>
      </div>
    );
  }

  if (!hasPageAccess()) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{customPage.name}</h1>
        {customPage.description && (
          <p className="text-gray-600">{customPage.description}</p>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Submission Name Field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Submission Name *
          </label>
          <input
            type="text"
            value={submissionName}
            onChange={(e) => setSubmissionName(e.target.value)}
            placeholder="Enter a name for this submission..."
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.name && (
            <p className="text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {customPage.fields.map((field, index) => (
          <div key={index} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            
            {renderField(field)}
            
            {errors[field.name] && (
              <p className="text-sm text-red-600">{errors[field.name]}</p>
            )}
            
            {field.type === 'date' && field.isDeadlineField && (
              <p className="text-sm text-blue-600">
                This field will be used to calculate task deadlines
              </p>
            )}
          </div>
        ))}

        {/* Checklist Section */}
        {getRequiredDocumentVerifications().length > 0 && (
          <div className="space-y-4 pt-6 border-t">
            <h3 className="text-lg font-medium text-gray-900">Required Checklist</h3>
            <p className="text-sm text-gray-600">
              The following documents are required for this submission. If no file is uploaded, the system will use the default file from the custom document:
            </p>
            
            <div className="space-y-4">
              {getRequiredDocumentVerifications().map((doc) => (
                <div key={doc._id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{doc.name}</h4>
                      {doc.description && (
                        <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Required
                      </span>
                      {documentUploads[doc._id] && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Custom Upload
                        </span>
                      )}
                      {!documentUploads[doc._id] && doc.file && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Using Default
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Show existing file if any */}
                  {documentUploads[doc._id] ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <span className="text-blue-600 text-xs font-medium">📄</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{documentUploads[doc._id].fileName}</p>
                            <p className="text-xs text-gray-500">
                              {documentUploads[doc._id].mimeType} • {documentUploads[doc._id].size ? `${(documentUploads[doc._id].size / 1024).toFixed(1)} KB` : 'Unknown size'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <a
                            href={documentUploads[doc._id].fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Download
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              handleDocumentUpload(doc._id, null);
                            }}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="text-center pt-2">
                        <input
                          type="file"
                          onChange={(e) => {
                            console.log('Replace File input change event:', e.target.files);
                            const files = Array.from(e.target.files);
                            console.log('Replace File array:', files);
                            handleFileUpload(doc._id, files);
                          }}
                          multiple
                          className="hidden"
                          id={`file-upload-replace-${doc._id}`}
                        />
                        <label
                          htmlFor={`file-upload-replace-${doc._id}`}
                          className="cursor-pointer inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Replace File
                        </label>
                      </div>
                    </div>
                  ) : (
                    /* Show upload interface when no files exist */
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-2">
                          Drag and drop files here or click to browse
                        </p>
                        <input
                          type="file"
                          onChange={(e) => {
                            console.log('File input change event:', e.target.files);
                            const files = Array.from(e.target.files);
                            console.log('Files array:', files);
                            handleFileUpload(doc._id, files);
                          }}
                          multiple
                          className="hidden"
                          id={`file-upload-${doc._id}`}
                        />
                        <label
                          htmlFor={`file-upload-${doc._id}`}
                          className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Choose Files
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {doc.category && (
                    <p className="text-xs text-gray-500 mt-2">
                      Category: {doc.category}
                    </p>
                  )}
                  
                  {doc.file && (
                    <p className="text-xs text-blue-600 mt-2">
                      ✓ Default file available: {doc.file.originalName || doc.file.name}
                      {!documentUploads[doc._id] && 
                        " (will be used if no file is uploaded)"}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end pt-6 border-t">
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomPageRenderer; 